import React from 'react';
import TabButton from './TabButton';
import TabDescription from './TabDescription';
import TabForm from './TabForm';

interface TabData {
    key: string;
    title: string;
    description: string;
    schema: any;
    uiSchema?: any;
    subTabs?: TabData[];
}

interface TabRendererProps {
    tabs: TabData[];
    activeTab: string;
    activeSubTabs: { [key: string]: string };
    formData: any;
    rootFormData: any;
    onTabChange: (key: string) => void;
    onSubTabChange: (parentKey: string, subKey: string) => void;
    onFormChange: (key: string, data: any) => void;
    onFormSubmit: (key: string, data: any) => void;
    onFormError: (error: any) => void;
    getFormData: (key: string) => any;
    level?: number;
}

const TabRenderer: React.FC<TabRendererProps> = ({
    tabs,
    activeTab,
    activeSubTabs,
    formData,
    rootFormData,
    onTabChange,
    onSubTabChange,
    onFormChange,
    onFormSubmit,
    onFormError,
    getFormData,
    level = 0,
}) => {
    const getTabClasses = (level: number) => {
        const classes = [
            'tab-button',
            'sub-tab-button',
            'nested-sub-tab-button',
            'deeper-nested-sub-tab-button',
        ];
        return classes[level] || 'tab-button';
    };

    const getContainerClasses = (level: number) => {
        const containers = [
            'settings-tabs',
            'sub-tabs',
            'nested-sub-tabs',
            'deeper-nested-sub-tabs',
        ];
        return containers[level] || 'settings-tabs';
    };

    const getDescriptionClasses = (level: number) => {
        const descriptions = [
            'tab-description',
            'sub-tab-description',
            'nested-sub-tab-description',
            'deeper-nested-sub-tab-description',
        ];
        return descriptions[level] || 'tab-description';
    };

    const renderTabs = () => {
        return (
            <div className={getContainerClasses(level)}>
                {tabs.map((tab) => (
                    <TabButton
                        key={tab.key}
                        isActive={activeTab === tab.key}
                        onClick={() => onTabChange(tab.key)}
                        title={tab.title}
                        className={getTabClasses(level)}
                    />
                ))}
            </div>
        );
    };

    const renderTabContent = () => {
        return tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            if (!isActive) return null;

            const getPaneClass = (level: number) => {
                const panes = [
                    'tab-pane',
                    'sub-tab-pane',
                    'nested-sub-tab-pane',
                    'deeper-nested-sub-tab-pane',
                ];
                return `${panes[level] || 'tab-pane'} active`;
            };

            return (
                <div key={tab.key} className={getPaneClass(level)}>
                    <TabDescription
                        title={tab.title}
                        description={tab.description}
                        className={getDescriptionClasses(level)}
                    />

                    {tab.subTabs && tab.subTabs.length > 0 ? (
                        <div className={getSubTabContainerClass(level)}>
                            <TabRenderer
                                tabs={tab.subTabs}
                                activeTab={activeSubTabs[tab.key] || tab.subTabs[0].key}
                                activeSubTabs={activeSubTabs}
                                formData={formData}
                                rootFormData={rootFormData}
                                onTabChange={(subKey) => onSubTabChange(tab.key, subKey)}
                                onSubTabChange={onSubTabChange}
                                onFormChange={onFormChange}
                                onFormSubmit={onFormSubmit}
                                onFormError={onFormError}
                                getFormData={getFormData}
                                level={level + 1}
                            />
                        </div>
                    ) : (
                        <TabForm
                            schema={tab.schema}
                            uiSchema={tab.uiSchema}
                            formData={getFormData(tab.key)}
                            rootFormData={rootFormData}
                            onChange={(data) => onFormChange(tab.key, data)}
                            onSubmit={(data) => onFormSubmit(tab.key, data)}
                            onError={onFormError}
                            saveButtonText={`Save ${tab.title}`}
                        />
                    )}
                </div>
            );
        });
    };

    const getSubTabContainerClass = (level: number) => {
        const containers = [
            'settings-tab-content',
            'sub-tabs-container',
            'nested-sub-tabs-container',
            'deeper-nested-sub-tabs-container',
        ];
        return containers[level + 1] || 'sub-tabs-container';
    };

    const getContentContainerClass = (level: number) => {
        const containers = [
            'settings-tab-content',
            'sub-tab-content',
            'nested-sub-tab-content',
            'deeper-nested-sub-tab-content',
        ];
        return containers[level] || 'sub-tab-content';
    };

    return (
        <>
            {renderTabs()}
            <div className={getContentContainerClass(level)}>{renderTabContent()}</div>
        </>
    );
};

export default TabRenderer;
